// src/components/layout/Navigation.jsx
import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700&display=swap');

  /* ── nav slide-down ── */
  @keyframes nav-in {
    from { opacity:0; transform: translateY(-8px); }
    to   { opacity:1; transform: translateY(0); }
  }
  .nav-animate { animation: nav-in .4s cubic-bezier(.22,.68,0,1.1) .2s both; }

  /* ── tab underline animation ── */
  .nav-tab {
    position: relative;
    transition: color .2s ease, background .2s ease;
  }
  .nav-tab::after {
    content: '';
    position: absolute;
    bottom: 0; left: 50%; right: 50%;
    height: 2px;
    border-radius: 99px 99px 0 0;
    background: linear-gradient(90deg, #6c63ff, #a78bfa);
    transition: left .25s cubic-bezier(.34,1.2,.64,1), right .25s cubic-bezier(.34,1.2,.64,1);
  }
  .nav-tab.active::after,
  .nav-tab:hover::after { left: 8px; right: 8px; }

  /* ── search ── */
  .nav-search {
    transition: width .3s cubic-bezier(.34,1.56,.64,1), box-shadow .2s ease;
  }
  .nav-search:focus {
    box-shadow: 0 0 0 3px rgba(108,99,255,.2);
    outline: none;
  }

  /* ── glass nav ── */
  .nav-glass {
    backdrop-filter: blur(16px) saturate(160%);
    -webkit-backdrop-filter: blur(16px) saturate(160%);
  }
`;

const Navigation = ({ activeTab, setActiveTab, isDarkMode, toggleDarkMode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const isAuthPage = location.pathname === '/signin' || location.pathname === '/signup';
  const isHomePage = location.pathname === '/';
  const showNavTabs = !isAuthPage && !isHomePage;

  const navItems = showNavTabs ? [
    { key: 'Home',     label: 'Home',     path: '/mainscreen',   icon: '🏠' },
    { key: 'My Apps',  label: 'My Apps',  path: '/mainscreen',   icon: '🤖' },
    { key: 'Usage',    label: 'Usage',    path: '/usage',        icon: '📊' },
    { key: 'Settings', label: 'Settings', path: '/system-access',icon: '⚙️' },
    { key: 'GoBot',    label: 'GoBot',    path: '/gobot',        icon: '✨' },
  ] : [];

  const handleTabClick = (item) => {
    if (['Home', 'My Apps'].includes(item.key)) {
      navigate('/mainscreen');
    } else if (item.key === 'Settings') {
      navigate('/system-access');
    } else if (item.key === 'GoBot') {
      navigate('/gobot');
    } else if (item.path) {
      navigate(item.path);
    }
    setActiveTab?.(item.key);
  };

  // On auth/home pages — render nothing (header handles everything)
  if (!showNavTabs) return (
    <>
      <style>{STYLES}</style>
      {/* invisible spacer so content clears the fixed header */}
      <div style={{ height: '68px' }} />
    </>
  );

  return (
    <>
      <style>{STYLES}</style>

      <nav
        className={`nav-animate nav-glass fixed left-0 w-full z-[90] transition-all duration-300 ${
          isDarkMode
            ? 'bg-[#0f0f24]/85 border-b border-white/8'
            : 'bg-white/70 border-b border-gray-200/70 shadow-sm'
        }`}
        style={{ top: '68px', height: '48px' }}
      >
        {/* subtle top separator line */}
        <div className={`absolute top-0 left-0 w-full h-px ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'}`} />

        <div className="flex items-center justify-between h-full max-w-screen-2xl mx-auto px-6 md:px-10">

          {/* ── Tab items ── */}
          <div className="flex items-center h-full gap-1">
            {navItems.map((item) => {
              const isActive = activeTab === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => handleTabClick(item)}
                  className={`nav-tab ${isActive ? 'active' : ''} h-full px-4 flex items-center gap-1.5 text-sm font-semibold rounded-t-lg transition-all duration-200
                    ${isActive
                      ? isDarkMode
                        ? 'text-violet-300 bg-white/5'
                        : 'text-violet-700 bg-violet-50/60'
                      : isDarkMode
                        ? 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
                        : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50/80'
                    }`}
                  style={{ fontFamily: 'Sora, sans-serif', fontSize: '13px' }}
                >
                  <span className="text-xs leading-none">{item.icon}</span>
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* ── Right: Search ── */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search
                className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors duration-200 ${
                  searchFocused
                    ? 'text-violet-500'
                    : isDarkMode ? 'text-gray-500' : 'text-gray-400'
                }`}
                style={{ width: 14, height: 14 }}
              />
              <input
                type="text"
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder="Search apps…"
                className={`nav-search h-8 pl-8 pr-4 rounded-xl border text-xs font-medium transition-all duration-300 placeholder-opacity-60
                  ${searchFocused ? 'w-56' : 'w-40'}
                  ${isDarkMode
                    ? 'bg-white/7 border-white/10 text-gray-200 placeholder-gray-500 focus:bg-white/10 focus:border-violet-500/50'
                    : 'bg-gray-100/80 border-gray-200 text-gray-700 placeholder-gray-400 focus:bg-white focus:border-violet-400'
                  }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              />
            </div>
          </div>
        </div>
      </nav>

      {/* spacer so page content clears both header + nav */}
      <div style={{ height: '116px' }} />
    </>
  );
};

export default Navigation;